import React, { useState } from 'react';
import { CheckCircle, Eye, XCircle, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { Alert } from 'dtos/alerts/alert.dto';
import { SeverityBadge, StatusBadge } from 'components/common/Badge';
import { Button } from 'components/common/Button';
import { EstimatedAmount } from 'components/common/EstimatedAmount';
import { ALERT_TYPE_LABELS } from 'constants/alertTypes';
import { formatDate } from 'utils/formatters';
import { useNavigate } from 'react-router-dom';

interface AlertCardProps {
  alert: Alert;
  onAcknowledge?: (id: string) => void;
  onResolve?: (id: string) => void;
  onDismiss?: (id: string) => void;
}

export function AlertCard({ alert, onAcknowledge, onResolve, onDismiss }: AlertCardProps) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const isOpen = alert.status === 'NEW' || alert.status === 'ACKNOWLEDGED';

  return (
    <div className={`bg-white rounded-lg border shadow-sm overflow-hidden ${
      alert.severity === 'CRITICAL' ? 'border-red-300' :
      alert.severity === 'HIGH' ? 'border-orange-300' :
      alert.severity === 'MEDIUM' ? 'border-yellow-200' : 'border-gray-200'
    }`}>
      {/* Header row */}
      <div
        className="px-5 py-4 flex items-start gap-4 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <SeverityBadge severity={alert.severity} />
            <span className="text-xs text-gray-500">{ALERT_TYPE_LABELS[alert.alert_type]}</span>
            <StatusBadge status={alert.status} />
          </div>
          <p className="text-sm font-semibold text-gray-900">{alert.title}</p>
          {alert.recommended_action && <p className="mt-1 text-sm text-gray-700"><span className="font-medium text-blue-700">What to do:</span> {alert.recommended_action}</p>}
          <p className="text-xs text-gray-500 mt-1">Found {formatDate(alert.created_at)}</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {alert.estimated_monthly_impact != null && (
            <EstimatedAmount amount={alert.estimated_monthly_impact} className="text-sm" />
          )}
          {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-gray-100 px-5 py-4 bg-gray-50 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">What happened</p>
              <p className="text-sm text-gray-700">{alert.what_happened}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Why it matters</p>
              <p className="text-sm text-gray-700">{alert.why_it_matters}</p>
            </div>
            {alert.estimated_monthly_impact != null && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Estimated impact</p>
                <EstimatedAmount amount={alert.estimated_monthly_impact} className="text-sm" />
              </div>
            )}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Recommended action</p>
              <p className="text-sm text-gray-700">{alert.recommended_action}</p>
            </div>
          </div>

          {isOpen && (
            <div className="flex flex-wrap gap-2 pt-2">
              {alert.entity_type === 'product' && (
                <Button size="sm" variant="secondary" onClick={() => navigate(`/products/${alert.entity_id}`)}>
                  <ExternalLink size={13} /> View product
                </Button>
              )}
              {alert.status === 'NEW' && onAcknowledge && (
                <Button size="sm" variant="secondary" onClick={() => onAcknowledge(alert.id)}>
                  <Eye size={13} /> Mark as seen
                </Button>
              )}
              {onResolve && (
                <Button size="sm" onClick={() => onResolve(alert.id)}>
                  <CheckCircle size={13} /> I fixed it
                </Button>
              )}
              {onDismiss && (
                <Button size="sm" variant="ghost" onClick={() => onDismiss(alert.id)}>
                  <XCircle size={13} /> Not relevant
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

